import React from 'react'
import { Col, Row } from 'react-styled-flexboxgrid'

import { FormBuilderV3 } from '@acorns/web-components'
import { pathOr } from 'ramda'
import styled from 'styled-components'

import SuccessPNG from 'assets/images/success.png'
import ConfirmationModal, {
  ButtonStyle,
  ModalActionTypes,
} from 'components/ConfirmationModal'
import SelectInput, { Option } from 'components/Form/SelectInput'
import TextInput from 'components/Form/TextInput/index'
import Spinner from 'components/Spinner'
import { withTrackClicked } from 'components/trackers'
import theme from 'theme'
import states from 'utils/states'

import AutoCompleteList from '../../screens/signup/address/AutoCompleteList'
import {
  ButtonsContainer,
  DirectDepositCard,
  Divider,
  EditButton,
  FieldLabel,
  FieldsContainer,
  GreenText,
  HeroText,
  HeroTitle,
  StyledButton,
  StyledHeading,
  StyledHero,
} from '../SharedComponents'
import {
  CancelButtonTapped,
  EditButtonTapped,
  NextButtonTapped,
  SubmitButtonTapped,
  SuccessModalViewed,
} from './segment'
import { InnerProps } from './types'

export const InputAndSuggestion = styled.div`
  position: relative;
  width: 100%;
`

const Hint = styled.span`
  font-size: 12px;
  padding-top: 15px;
`

const StyledEditButton = styled(EditButton)`
  &&& {
    top: 0;
    right: 0;
    position: relative;
    float: right;
  }
`

export function formSubmitOverride(state, dirty, isSubmitting) {
  if (state && state.switchType === 'submittable' && !state.requireAddress) {
    return false
  }
  return !dirty || isSubmitting
}

export const NextButton = withTrackClicked<any, any>((props) =>
  NextButtonTapped(props.employerName),
)(StyledButton)

export const CancelButton = withTrackClicked<any, any>((props) =>
  CancelButtonTapped(props.employerName),
)(StyledButton)

export const SubmitButton = withTrackClicked<any, any>((props) =>
  SubmitButtonTapped(props.employerName),
)(StyledButton)

export const TrackedEditButton = withTrackClicked<any, any>((props) =>
  EditButtonTapped(props.employerName),
)(StyledEditButton)

const SSNInfoTooltipContainer = styled.div`
  position: absolute;
  display: inline-block;

  &:hover,
  &:focus {
    .ssn-tooltip-message {
      display: block;
    }
  }
`

const SSNInfoTooltipButton = styled.div`
  border: 1.5px solid ${theme.colors.stone};
  border-radius: 50%;
  width: 18px;
  height: 18px;
  color: ${theme.colors.stone};
  margin-left: 10px;
  padding: 0;
  font-size: 11px;
  text-align: center;
  font-weight: bold;
`

const SSNInfoTooltipText = styled.div`
  position: absolute;
  font-size: 12px;
  line-height: 1.5;
  color: ${theme.colors.slate};
  width: 332px;
  box-shadow: ${theme.shadows.lgMedium};
  background-color: #ffffff;
  padding: 8px 16px;
  left: 40px;
  top: -17px;
  display: none;
  border-radius: 4px;

  &:before {
    content: '';
    display: inline-block;
    position: absolute;
    left: -6px;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 6px solid white;
    top: 20px;
    bottom: unset;
  }

  @media screen and (max-width: ${theme.breakpoints.md - 1}px) {
    width: calc(100vw - 110px);
    left: -95px;
    top: unset;
    bottom: 30px;

    &:before {
      content: '';
      left: 108px;
      border-right: 6px solid transparent;
      border-left: 6px solid transparent;
      border-top: 6px solid white;
      bottom: -6px;
      top: unset;
      border-bottom: unset;
    }
  }

  @media screen and (max-width: ${theme.breakpoints.sm - 1}px) {
    width: calc(100vw - 92px);
  }
`

const EditFieldLabel = styled(FieldLabel)`
  color: ${theme.colors.stone};
  display: block;
  font-size: 12px;
  font-weight: normal;
  line-height: 2;
`

const EditFieldValue = styled(FieldLabel)`
  color: ${theme.colors.slate};
  display: block;
  font-size: 14px;
  font-weight: normal;
  line-height: 1.43;
`

const EditRow = styled(Row)`
  padding-bottom: 30px;
`

const SSNInfoTooltip = () => (
  <SSNInfoTooltipContainer>
    <SSNInfoTooltipButton>?</SSNInfoTooltipButton>
    <SSNInfoTooltipText className="ssn-tooltip-message">
      All of your data is protected with 256-bit encryption and never stored on
      your phone, tablet or computer.
    </SSNInfoTooltipText>
  </SSNInfoTooltipContainer>
)

export const Hero = () => (
  <StyledHero secondary={true}>
    <HeroTitle>Review Employer Information</HeroTitle>
    <HeroText>
      Please confirm the information below or add any missing details. If the
      employer name is wrong, please return to the “Search” screen.
    </HeroText>
  </StyledHero>
)

const SubmittableEmployerForm = (props) => {
  return (
    <>
      <FieldsContainer>
        <StyledHeading>Employer's Info</StyledHeading>
        <FormBuilderV3
          errors={props.errors}
          rows={props.employerInfoRows}
          values={props.values}
          handleChange={props.handleChange}
          handleSelectChange={props.handleSelectChange}
          loading={false}
        />
      </FieldsContainer>
      <Divider />
      {(props.location.state.switchType !== 'submittable' ||
        props.location.state.requireAddress) && (
        <FieldsContainer>
          <StyledHeading>Employer's Address</StyledHeading>

          <Row>
            <Col xs={12} sm={12} md={6}>
              <InputAndSuggestion>
                <TextInput
                  type="text"
                  autocomplete="off"
                  error={!!props.errors.address1}
                  errorText={props.errors.address1}
                  id="address1"
                  innerRef={props.setupObservable}
                  name="address1"
                  label="Address"
                  onChange={props.handleChange}
                  required={true}
                  value={props.values.address1}
                  placeholder="123 Street"
                />
                {props.addressAutoCompleteOptions && (
                  <AutoCompleteList
                    suggestions={props.addressAutoCompleteOptions}
                    onClickOutside={props.handleClickOutside}
                    onSelect={props.handleAutoCompleteSelect}
                  />
                )}
              </InputAndSuggestion>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <TextInput
                type="text"
                autocomplete="off"
                id="address2"
                label="Address (Line 2)"
                name="address2"
                onChange={props.handleChange}
                required={false}
                value={props.values.address2}
                placeholder="Apt. #123"
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={12} md={6}>
              <TextInput
                type="text"
                autocomplete="off"
                error={!!props.errors.city}
                errorText={props.errors.city}
                id="city"
                label="City"
                onChange={props.handleChange}
                name="city"
                required={true}
                value={props.values.city}
                placeholder="Irvine"
              />
            </Col>
            <Col xs={12} sm={12} md={6}>
              <SelectInput
                id="state"
                onChange={props.handleSelectChange}
                error={!!props.errors.state}
                errorText={props.errors.state}
                name="state"
                label="State"
                options={
                  states.map(({ label }) => ({
                    label,
                    value: label,
                  })) as Option[]
                }
                value={props.values.state}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={12}>
              <TextInput
                type="text"
                autocomplete="off"
                error={!!props.errors.zip}
                errorText={props.errors.zip}
                id="zip"
                label="Zip"
                onChange={props.handleChange}
                name="zip"
                required={true}
                value={props.values.zip}
                placeholder="92610"
              />
            </Col>
          </Row>
        </FieldsContainer>
      )}
      <Divider />
      <FieldsContainer>
        <StyledHeading>Social Security Number</StyledHeading>
        <Row>
          <Col xs={12} sm={12}>
            <TextInput
              type="text"
              autocomplete="off"
              id="ssnLastFour"
              label="SSN *"
              onChange={props.handleChange}
              name="ssnLastFour"
              locked={true}
              value={`****-**-${props.ssnLastFour}`}
              helperText={
                <Hint>
                  All of your data is protected with 256-bit encryption and
                  never stored on your phone, tablet or computer.
                </Hint>
              }
            />
          </Col>
        </Row>
      </FieldsContainer>
    </>
  )
}

const SubmittableEmployerView = (props: InnerProps) => (
  <FieldsContainer>
    <EditRow>
      <Col xs={12} sm={12} md={5}>
        <EditFieldLabel>Name</EditFieldLabel>
        <EditFieldValue>{props.employerName}</EditFieldValue>
      </Col>

      <Col xs={12} sm={12} md={5}>
        {props.values.address1 && (
          <>
            <EditFieldLabel>Address</EditFieldLabel>
            <EditFieldValue>
              {props.values.address1} {props.values.address2}
              <br />
              {props.values.city}, {props.values.state} {props.values.zip}
              <br />
              United States
            </EditFieldValue>
          </>
        )}
      </Col>

      <Col xs={12} sm={12} md={2}>
        <TrackedEditButton onPress={props.handleEdit} secondary={true}>
          Edit
        </TrackedEditButton>
      </Col>
    </EditRow>
    <EditRow>
      <Col xs={12} sm={12} md={5}>
        <EditFieldLabel>Deposit Percentage</EditFieldLabel>
        <EditFieldValue>
          <GreenText>100%</GreenText>
        </EditFieldValue>
      </Col>
      <Col xs={12} sm={12} md={5}>
        <EditFieldLabel>Deposit to:</EditFieldLabel>
        <EditFieldValue>
          Spend Account - <GreenText>{props.balance}</GreenText>
        </EditFieldValue>
      </Col>
    </EditRow>
    <EditRow>
      <Col xs={12} sm={12}>
        <EditFieldLabel>SSN</EditFieldLabel>
        <EditFieldValue>
          &#8226; &#8226; &#8226; - &#8226; &#8226; - {props.ssnLastFour}{' '}
          <SSNInfoTooltip />
        </EditFieldValue>
      </Col>
    </EditRow>
  </FieldsContainer>
)

export const SubmittableEmployer = (props: InnerProps) => (
  <DirectDepositCard>
    {props.editing && <SubmittableEmployerForm {...props} />}
    {!props.editing && <SubmittableEmployerView {...props} />}
    <Divider />
    <ButtonsContainer>
      {!props.editing && (
        <SubmitButton
          onPress={props.handleSubmitSwitch}
          employerName={props.values && props.values.name}
          disabled={formSubmitOverride(
            pathOr(null, ['location', 'state'], props),
            props.dirty,
            props.isSubmitting,
          )}
        >
          {props.processing ? <Spinner height={40} width={40} /> : 'Submit'}
        </SubmitButton>
      )}
      {props.editing && (
        <>
          <NextButton
            onPress={props.handleSubmit}
            disabled={formSubmitOverride(
              pathOr(null, ['location', 'state'], props),
              props.dirty,
              props.isSubmitting,
            )}
            employerName={props.values && props.values.name}
          >
            Next
          </NextButton>

          <CancelButton
            onPress={props.handleCancel}
            secondary={true}
            employerName={props.values && props.values.name}
          >
            Cancel
          </CancelButton>
        </>
      )}
    </ButtonsContainer>
  </DirectDepositCard>
)

export const SuccessModal = (props: InnerProps) => (
  <ConfirmationModal
    title={'Woohoo'}
    image={SuccessPNG}
    body={
      'You’ve set up direct deposit with a new employer! It can take 7 to 21 business days for direct deposit to finalize. We’ll let you know when it’s complete.'
    }
    isOpen={props.showConfirmModal}
    buttonStyle={ButtonStyle.col}
    eventFields={SuccessModalViewed}
    actions={[
      {
        handler: props.handleModalContinue,
        label: 'Set Up Another Direct Deposit',
        type: ModalActionTypes.primary,
      },
      {
        handler: props.handleModalClose,
        label: 'Maybe Later',
        type: ModalActionTypes.secondary,
      },
    ]}
  />
)
